import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Search, BookOpen } from 'lucide-react';
import { hadithApi } from '../services/api';
import { Input } from '../components/ui/input';
import { PageLoading, GlassCard } from '../components/shared';

const HadithBooks = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      hadithApi.getCollection(slug),
      hadithApi.getBooks(slug),
    ])
      .then(([c, b]) => {
        if (cancelled) return;
        setCollection(c.data);
        setBooks(b.data);
      })
      .catch(console.error)
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  const filtered = useMemo(() => {
    if (!q.trim()) return books;
    const needle = q.toLowerCase();
    return books.filter(b =>
      (b.name_en || '').toLowerCase().includes(needle) ||
      (b.name_ar || '').includes(q) ||
      String(b.book_number) === q.trim()
    );
  }, [books, q]);

  if (loading) return <PageLoading />;
  if (!collection) return null;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="hadith-books-page">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/hadith"
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-primary mb-6"
          data-testid="hadith-back-to-collections"
        >
          <ChevronLeft className="w-4 h-4" /> {t('hadith.all_collections')}
        </Link>

        <div className="mb-8">
          <p className="font-arabic text-4xl text-text-primary text-right" dir="rtl">
            {collection.name_ar}
          </p>
          <h1 className="text-3xl sm:text-4xl font-serif text-text-primary mt-2">
            {collection.name_fr || collection.name_en}
          </h1>
          <p className="text-sm text-text-muted mt-1">
            {t('hadith.books_kutub', { total: collection.total_hadiths.toLocaleString(), books: books.length })}
          </p>
        </div>

        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            type="text"
            placeholder={t('hadith.search_book_placeholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
            data-testid="hadith-books-search-input"
          />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b, idx) => (
            <motion.div
              key={b.book_number}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.3) }}
            >
              <Link
                to={`/hadith/${slug}/book/${b.book_number}`}
                data-testid={`hadith-book-card-${b.book_number}`}
              >
                <GlassCard className="hover:border-primary/40 transition-all cursor-pointer h-full">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <span className="overline">{t('hadith.book_label', { number: b.book_number })}</span>
                    <BookOpen className="w-4 h-4 text-text-muted" />
                  </div>
                  <p className="font-arabic text-xl text-text-primary text-right leading-snug" dir="rtl">
                    {b.name_ar}
                  </p>
                  <p className="text-sm text-text-secondary mt-2">{b.name_en}</p>
                  <p className="text-xs text-text-muted mt-3">
                    {b.hadith_count} {b.hadith_count > 1 ? t('hadith.hadith_plural') : t('hadith.hadith_singular')}
                  </p>
                </GlassCard>
              </Link>
            </motion.div>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-text-muted py-12">{t('hadith.no_book_match')}</p>
        )}
      </div>
    </div>
  );
};

export default HadithBooks;
